import React from "react";
import { useLang } from "@/lib/i18n";
import { ASSETS } from "@/lib/assets";
import { ShieldCheck, TrendingUp, ArrowRight, Lock, Check } from "lucide-react";
import CountUp from "./CountUp";

export default function Hero({ stats, settings }) {
  const { t, lang } = useLang();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const bullets = [
    `${settings.display_win_rate}% ${t("hero.trustWinrate")} · ${settings.display_total_tips}+ ${t("hero.trustTips")}`,
    t("hero.bulletDelivery"),
    t("hero.bulletArchive"),
  ];

  return (
    <section id="top" className="relative overflow-hidden pt-28 lg:pt-32 pb-16 lg:pb-24" style={{ background: "var(--at-bg)" }}>
      <div className="absolute inset-0 pitch-grid-tactical opacity-60 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(130% 90% at 80% 10%, rgba(217,20,56,0.35) 0%, transparent 55%)" }} />
      {/* oversized watermark */}
      <div className="absolute -top-2 left-0 right-0 overflow-hidden pointer-events-none select-none leading-none">
        <div className="font-serif-display font-bold text-white/[0.05] text-[22vw] lg:text-[16vw] whitespace-nowrap -ml-4">
          AUSTRIA TIPS
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
        {/* LEFT editorial column */}
        <div className="animate-fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">01</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">
              {t("hero.eyebrow")}
            </span>
          </div>

          <h1 className="font-serif-display font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {lang === "de" ? settings.hero_title_de : settings.hero_title_en}
          </h1>

          <p className="mt-6 text-base sm:text-lg font-body text-white/80 max-w-xl leading-relaxed">
            {lang === "de" ? settings.hero_subtitle_de : settings.hero_subtitle_en}
          </p>

          <ul className="mt-7 space-y-2.5 max-w-lg">
            {bullets.map((b, i) => (
              <li key={i} data-testid={`hero-bullet-${i}`} className="flex items-start gap-2.5">
                <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}>
                  <Check size={12} />
                </span>
                <span className="text-sm font-body text-white/85 font-medium">{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("pricing")}
              className="group inline-flex items-center justify-center gap-2 text-[#38040C] font-bold font-heading px-7 py-3.5 rounded-full text-sm transition-transform hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(255,215,0,0.3)]"
              style={{ background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" }}
            >
              <ShieldCheck size={17} /> {t("hero.ctaPrimary")}
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("free-tip")}
              className="group inline-flex items-center justify-center gap-2 font-bold font-heading px-7 py-3.5 rounded-full text-sm border border-[var(--border-gold)] text-white hover:bg-white/5 transition-colors"
            >
              {t("hero.ctaSecondary")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* trust ticker with count-up */}
          <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg pt-8 border-t border-[var(--border-light)]">
            <div data-testid="hero-stat-winrate">
              <CountUp value={settings.display_win_rate} suffix="%" className="font-serif-display font-bold text-3xl sm:text-4xl gold-gradient-text" />
              <div className="text-[10px] font-heading font-semibold uppercase tracking-wider text-white/55 mt-1">{t("hero.trustWinrate")}</div>
            </div>
            <div data-testid="hero-stat-tips" className="pl-4 border-l border-[var(--border-light)]">
              <CountUp value={settings.display_total_tips} suffix="+" className="font-serif-display font-bold text-3xl sm:text-4xl text-white" />
              <div className="text-[10px] font-heading font-semibold uppercase tracking-wider text-white/55 mt-1">{t("hero.trustTips")}</div>
            </div>
            <div data-testid="hero-stat-years" className="pl-4 border-l border-[var(--border-light)]">
              <CountUp value={settings.years_experience} suffix="+" className="font-serif-display font-bold text-3xl sm:text-4xl text-white" />
              <div className="text-[10px] font-heading font-semibold uppercase tracking-wider text-white/55 mt-1">{t("hero.trustYears")}</div>
            </div>
          </div>
        </div>

        {/* RIGHT portrait */}
        <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative mx-auto max-w-sm lg:max-w-md">
            <div className="absolute -inset-6 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(217,20,56,0.4), transparent 68%)" }} />

            <div data-testid="hero-portrait-card" className="corner-bracket relative rounded-2xl overflow-hidden ring-1 ring-[var(--border-gold)]"
                 style={{ boxShadow: "0 30px 70px -20px rgba(0,0,0,0.7)" }}>
              <div className="absolute inset-0 p-1.5 rounded-2xl pointer-events-none" style={{ border: "1px solid rgba(255,255,255,0.12)" }} />
              <img src={ASSETS.portrait} alt="Markus Huber – Austria Tips" data-testid="hero-portrait-image" className="w-full h-[430px] sm:h-[520px] object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(56,4,12,0) 42%, rgba(56,4,12,0.9) 100%)" }} />

              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-3">
                <div>
                  <div className="font-serif-display font-bold text-white text-2xl leading-none">Markus Huber</div>
                  <div className="text-[11px] font-heading uppercase tracking-wider text-[var(--trust-gold)] mt-1.5">{t("hero.founderRole")}</div>
                </div>
                <img src={ASSETS.signature} alt="signature" className="h-11 w-auto sig-blend" data-testid="hero-signature" />
              </div>
            </div>

            {/* top verified badge */}
            <div data-testid="hero-verified-badge" className="animate-float absolute -top-4 -right-3 sm:-right-5 at-card-solid rounded-xl px-4 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[#38040C]" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}>
                <ShieldCheck size={17} />
              </div>
              <div>
                <div className="text-[11px] font-bold font-heading text-white leading-tight">#1 Austrian</div>
                <div className="text-[9px] text-white/60 font-body uppercase tracking-wide">Verified Tipster</div>
              </div>
            </div>

            {/* bottom win-rate pill */}
            <div data-testid="floating-winrate-pill" className="animate-float absolute -bottom-4 -left-3 sm:-left-6 at-card-solid rounded-full px-4 py-2.5 flex items-center gap-2.5" style={{ animationDelay: "1s" }}>
              <span className="w-2.5 h-2.5 rounded-full bg-[var(--pitch-green)] pulse-dot" />
              <div className="text-xs font-heading font-bold text-white">
                {settings.display_win_rate}% <span className="text-white/60 font-medium">{t("hero.trustWinrate")}</span>
              </div>
            </div>
          </div>

          {/* unlock banner */}
          <button
            data-testid="hero-unlock-banner"
            onClick={() => scrollTo("pricing")}
            className="relative mt-8 w-full max-w-md mx-auto flex items-center justify-center gap-2 py-4 rounded-xl text-[#38040C] font-heading font-extrabold tracking-wide text-sm uppercase transition-transform hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(255,215,0,0.3)]"
            style={{ background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" }}
          >
            <Lock size={16} /> {lang === "de" ? settings.hero_banner_text_de : settings.hero_banner_text_en}
          </button>

          {/* verified tipsters */}
          <a
            href="https://www.verified-tipsters.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-verified-tipsters"
            className="group mt-5 flex items-center justify-center gap-2.5 max-w-md mx-auto rounded-xl border border-[var(--border-light)] bg-white/5 px-4 py-3 hover:border-[var(--border-gold)] transition-colors"
          >
            <img src="/verified-tipsters-logo.svg" alt="Verified Tipsters" className="h-8 w-8 shrink-0 rounded-lg ring-1 ring-white/20" />
            <span className="text-[11px] leading-snug font-heading font-semibold text-white/80 group-hover:text-white transition-colors">
              {lang === "de" ? "Verifizierter Fußball-Tippgeber auf verified-tipsters.com" : "Verified football predictions tipster on verified-tipsters.com"}
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
